import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

// Sticky left-side scroll indicator for long-scroll views. Each section gets
// a labeled dot on a vertical track. As the scroll root advances past a
// section, the progress line fills and the active dot pops. Click a label to
// smooth-scroll to that section.
//
// Ported from UtilityDesk (src/components/ScrollNavRail.tsx). Uses only `cn`
// + house tokens + `.eyebrow`, all present here.
//
// Assumptions:
//   - Rendered inside a scrollable ancestor; caller passes `scrollRoot` so
//     the IntersectionObserver knows which container to watch.
//   - Each target section has a DOM id matching a SectionDef.id.

export interface SectionDef {
  id: string;
  label: string;
}

interface Props {
  sections: SectionDef[];
  scrollRoot: HTMLElement | null;
  className?: string;
}

const ROW_PX = 30;

export function ScrollNavRail({ sections, scrollRoot, className }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const suppressObserverRef = useRef(false);

  useEffect(() => {
    if (!scrollRoot || sections.length === 0) return;

    const targets = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressObserverRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        // Prefer the topmost visible section — matches what the reader is
        // anchored on at the current scroll position.
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const topId = visible[0]!.target.id;
        const idx = sections.findIndex((s) => s.id === topId);
        if (idx !== -1) setActiveIdx(idx);
      },
      {
        root: scrollRoot,
        // Activation zone: the band between 30% from top and 50% from
        // bottom. Keeps the active-mark from whipping as cards slide
        // through the viewport.
        rootMargin: '-30% 0px -50% 0px',
        threshold: 0,
      },
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [sections, scrollRoot]);

  const handleClick = (idx: number, id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Optimistically set active so the UI responds immediately; suppress the
    // observer briefly so it doesn't fight the smooth scroll.
    setActiveIdx(idx);
    suppressObserverRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      suppressObserverRef.current = false;
    }, 800);
  };

  // Progress line height: from first dot (center) to active dot (center).
  const progressPx = activeIdx * ROW_PX;

  return (
    <nav
      className={cn('w-44 shrink-0 sticky top-8 self-start', className)}
      aria-label="Section navigation"
    >
      <div className="eyebrow text-[9px] mb-2 pl-1">On this page</div>
      <div className="relative">
        {/* Track (full span between first and last dot centers) */}
        <div
          className="absolute left-[4.65px] w-[1.5px] bg-border"
          style={{
            top: `${ROW_PX / 2}px`,
            height: `${(sections.length - 1) * ROW_PX}px`,
          }}
        />
        {/* Progress (fills top → active dot). duration-500 (default scale) —
            the source used a 400 step that isn't in this app's Tailwind config,
            and the arbitrary 400ms form is ambiguous (matches both transition
            and animation duration); 500ms reads identically here. */}
        <div
          className="absolute left-[4.65px] w-[1.5px] bg-primary transition-[height] duration-500 ease-out"
          style={{
            top: `${ROW_PX / 2}px`,
            height: `${progressPx}px`,
          }}
        />
        {sections.map((s, i) => {
          const isActive = i === activeIdx;
          return (
            <button
              key={s.id}
              onClick={() => handleClick(i, s.id)}
              style={{ height: `${ROW_PX}px` }}
              className={cn(
                'relative flex items-center gap-2.5 w-full pr-2 text-left transition-colors group',
                isActive
                  ? 'text-primary font-semibold'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <span
                className={cn(
                  'relative z-10 h-[10.8px] w-[10.8px] rounded-full border-[1.5px] shrink-0 transition-all',
                  isActive
                    ? 'bg-primary border-primary ring-[3.6px] ring-primary/15 scale-110'
                    : 'bg-card border-border group-hover:border-primary/40',
                )}
              />
              <span className="text-[11px] leading-tight truncate">{s.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
